
/** Parallel to `segments`. Omitted when every line is part of the route. */
export type DecorativeFlags = boolean[];

function decorativeFlag(value: unknown): boolean {
  return value === true || value === 1;
}

export function alignDecorative(raw: unknown, count: number): DecorativeFlags {
  const out: DecorativeFlags = new Array(count).fill(false);
  if (!Array.isArray(raw)) return out;
  for (let i = 0; i < count && i < raw.length; i++) out[i] = decorativeFlag(raw[i]);
  return out;
}

export function packDecorative(flags: readonly boolean[], count: number): DecorativeFlags | undefined {
  for (let i = 0; i < count; i++) {
    if (flags[i]) return flags.slice(0, count).map((value) => value === true);
  }
  return undefined;
}

export function cloneDecorative(flags: readonly boolean[], count: number): DecorativeFlags {
  return alignDecorative(flags, count);
}

export function isDecorative(flags: readonly boolean[] | undefined, index: number): boolean {
  return decorativeFlag(flags?.[index]);
}

/** True when every incident segment at `vertex` is decorative (장식선). */
export function touchesOnlyDecorativeSegments(
  vertex: number,
  segments: ReadonlyArray<readonly [number, number]>,
  decorative?: readonly boolean[],
): boolean {
  let walkable = 0;
  let decor = 0;
  for (let s = 0; s < segments.length; s++) {
    const seg = segments[s];
    if (!seg || (seg[0] !== vertex && seg[1] !== vertex)) continue;
    if (isDecorative(decorative, s)) decor += 1;
    else walkable += 1;
  }
  return decor > 0 && walkable === 0;
}

/** Segments that count toward distance, walking order, connectivity, and road checks. */
export function segmentsForRoute(
  segments: ReadonlyArray<readonly [number, number]>,
  decorative: readonly boolean[] | undefined,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < segments.length; i++) {
    if (isDecorative(decorative, i)) continue;
    const seg = segments[i];
    if (!seg) continue;
    out.push([seg[0], seg[1]]);
  }
  return out;
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

export function remapDecorative(
  flags: readonly boolean[],
  oldSegments: ReadonlyArray<readonly [number, number]>,
  newSegments: ReadonlyArray<readonly [number, number]>,
  pointMap: Int32Array | null,
): DecorativeFlags {
  const out: DecorativeFlags = new Array(newSegments.length).fill(false);
  if (!pointMap) return out;
  const buckets = new Map<string, boolean>();
  for (let i = 0; i < oldSegments.length; i++) {
    const pair = oldSegments[i];
    if (!pair || !flags[i]) continue;
    const a = pointMap[pair[0]] ?? -1;
    const b = pointMap[pair[1]] ?? -1;
    if (a < 0 || b < 0 || a === b) continue;
    buckets.set(edgeKey(a, b), true);
  }
  for (let i = 0; i < newSegments.length; i++) {
    const pair = newSegments[i];
    if (!pair) continue;
    if (buckets.get(edgeKey(pair[0], pair[1])) === true) out[i] = true;
  }
  return out;
}

export function joinedDecorative(
  oldSegments: ReadonlyArray<readonly [number, number]>,
  oldFlags: readonly boolean[],
  newSegments: ReadonlyArray<readonly [number, number]>,
  order: readonly number[],
): DecorativeFlags {
  const out: DecorativeFlags = new Array(newSegments.length).fill(false);
  const kept = Math.min(oldSegments.length, newSegments.length);
  for (let i = 0; i < kept; i++) out[i] = oldFlags[i] === true;
  const incident = new Map<number, boolean>();
  for (let i = 0; i < oldSegments.length; i++) {
    if (!oldFlags[i]) continue;
    const pair = oldSegments[i];
    if (!pair) continue;
    incident.set(pair[0], true);
    incident.set(pair[1], true);
  }
  const seen = new Set<string>();
  for (const pair of oldSegments) {
    if (!pair || pair[0] === pair[1]) continue;
    seen.add(edgeKey(pair[0], pair[1]));
  }
  let cursor = oldSegments.length;
  for (let i = 1; i < order.length && cursor < newSegments.length; i++) {
    const a = order[i - 1];
    const b = order[i];
    if (a == null || b == null || a === b) continue;
    const key = edgeKey(a, b);
    if (seen.has(key)) continue;
    seen.add(key);
    out[cursor] = incident.get(a) === true || incident.get(b) === true;
    cursor += 1;
  }
  return out;
}

export function dissolvedDecorative(
  oldFlags: readonly boolean[],
  oldSegments: ReadonlyArray<readonly [number, number]>,
  newSegments: ReadonlyArray<readonly [number, number]>,
  pointMap: Int32Array | null,
  collapsedFrom: readonly (readonly number[])[],
): DecorativeFlags {
  const out: DecorativeFlags = new Array(newSegments.length).fill(false);
  const survived = new Map<string, boolean>();
  if (pointMap) {
    for (let i = 0; i < oldSegments.length; i++) {
      const pair = oldSegments[i];
      if (!pair || !oldFlags[i]) continue;
      const a = pointMap[pair[0]] ?? -1;
      const b = pointMap[pair[1]] ?? -1;
      if (a < 0 || b < 0 || a === b) continue;
      survived.set(edgeKey(a, b), true);
    }
  }
  for (let i = 0; i < newSegments.length; i++) {
    const pair = newSegments[i];
    if (!pair) continue;
    if (survived.get(edgeKey(pair[0], pair[1])) === true) {
      out[i] = true;
      continue;
    }
    const group = collapsedFrom[i];
    if (!group) continue;
    for (const oldI of group) {
      if (oldFlags[oldI] === true) {
        out[i] = true;
        break;
      }
    }
  }
  return out;
}

export function toggleDecorativeOnLines(
  flags: readonly boolean[],
  lineCount: number,
  lineIndices: readonly number[],
  value?: boolean,
): { decorative: DecorativeFlags; changed: boolean } {
  const next = cloneDecorative(flags, lineCount);
  if (lineIndices.length === 0) return { decorative: next, changed: false };
  const target =
    value ??
    !lineIndices.some((index) => index >= 0 && index < lineCount && next[index] === true);
  let changed = false;
  for (const index of lineIndices) {
    if (index < 0 || index >= lineCount) continue;
    if (next[index] !== target) {
      next[index] = target;
      changed = true;
    }
  }
  return { decorative: next, changed };
}

export interface DecorativeFormSlice {
  decorative: boolean;
  decorativeMixed: boolean;
}

export function decorativeFormValues(
  flags: readonly boolean[],
  lineCount: number,
  lineIndices: readonly number[],
): DecorativeFormSlice {
  if (lineIndices.length === 0) return { decorative: false, decorativeMixed: false };
  let seen = false;
  let value = false;
  let mixed = false;
  for (const index of lineIndices) {
    if (index < 0 || index >= lineCount) continue;
    const flag = flags[index] === true;
    if (!seen) {
      seen = true;
      value = flag;
      continue;
    }
    if (flag !== value) mixed = true;
  }
  return { decorative: seen ? value : false, decorativeMixed: mixed };
}
